import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { productService } from '../../services/api';
import ProductCard from '../../components/ProductCard/ProductCard';
import styles from './SearchResults.module.css';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (query.trim()) {
      searchProducts(query, currentPage);
    }
  }, [query, currentPage]);

  const searchProducts = async (searchTerm, page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await productService.searchProducts({
        q: searchTerm,
        per_page: 12,
        page: page
      });

      if (response.success) {
        setProducts(response.data.data || []);
        setPagination({
          current_page: response.data.current_page || 1,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 12,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || 'Erreur lors de la recherche');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la recherche');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!query.trim()) {
    return (
      <div className={styles.searchResults}>
        <div className={styles.emptyState}>
          <h2>Recherche vide</h2>
          <p>Veuillez entrer un terme de recherche.</p>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.searchResults}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Résultats de recherche</h1>
          <p className={styles.query}>
            Recherche pour : <strong>"{query}"</strong>
            {pagination && (
              <span className={styles.count}>
                ({pagination.total} résultat{pagination.total !== 1 ? 's' : ''})
              </span>
            )}
          </p>
        </div>

        {isLoading ? (
          <div className={styles.loading}>
            <p>Recherche en cours...</p>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className={styles.backButton}>
              Retour à l'accueil
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>Aucun résultat trouvé</h2>
            <p>Aucun produit ne correspond à votre recherche "{query}".</p>
            <button onClick={() => navigate('/')} className={styles.backButton}>
              Retour à l'accueil
            </button>
          </div>
        ) : (
          <>
            <div className={styles.productsGrid}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {pagination && pagination.last_page > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  Précédent
                </button>

                <span className={styles.paginationInfo}>
                  Page {currentPage} sur {pagination.last_page}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.last_page}
                  className={styles.paginationButton}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchResults;

